import React from 'react';
import { Rect, SchematicShell } from './SchematicPrimitives';
import { SURFACE_OUTLET } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Surface receptacle — the 2D twin of `surfaceOutletRenderer`. Both read
 * `SURFACE_OUTLET` in proceduralSpecs, so the flange and lid keep the same
 * proportions on paper and in 3D.
 *
 * This is the one device in the tray that mounts in a HORIZONTAL surface (a
 * floor slab or a countertop), so the profile is the mirror of the ceiling
 * devices: the support surface is the BOTTOM of the bbox (SVG y = height) and
 * the lid stands proud of it, upward. Drawn as a section — the flange ring
 * flush with the surface, the lid disc above it.
 *
 * Radially symmetric, so every view is the same profile over its own
 * horizontal extent.
 *
 * SVG y runs DOWN, so "above the surface" means SMALLER y.
 */
export function SurfaceOutletSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const horizontal = view === 'left' || view === 'right' ? depth : width;
  const radius = Math.min(width, depth) / 2;
  const cx = horizontal / 2;
  const coverH = height * SURFACE_OUTLET.coverHeightFrac;
  const lidR = radius * SURFACE_OUTLET.lidRadiusFrac;
  const lidH = height * SURFACE_OUTLET.lidHeightFrac;
  const stroke = { strokeColor, strokeWidth };

  return (
    <SchematicShell
      width={horizontal}
      height={height}
      drawFrame={false}
      {...stroke}
    >
      {/* Flange, flush with the surface: sits on the bbox bottom. */}
      <Rect
        x={cx - radius}
        y={height - coverH}
        width={radius * 2}
        height={coverH}
        {...stroke}
      />
      {/* Lid, proud of the flange. */}
      <Rect
        x={cx - lidR}
        y={height - coverH - lidH}
        width={lidR * 2}
        height={lidH}
        {...stroke}
      />
    </SchematicShell>
  );
}
SurfaceOutletSchematic.supportsSideViews = true;
